import moment from 'moment'
import { ITrailerShipmentDetails, Maybe } from 'types/FMSGraphqlTypes'
import { ITrailerStatesEnum, IShipmentStates } from 'types/graphqlTypes'
import { FORMATS } from 'constants/datetime'
import { DISPLAY } from 'constants/display'

export const getLocation = (facilityName?: string, shipmentState?: string) => {
  if (!facilityName) {
    return '—'
  }

  return shipmentState === 'delivered' ? `Delivered: ${facilityName}` : facilityName
}

export const getLoadId = (loadId?: string) => {
  if (!loadId) {
    return '—'
  }

  return `TFX-${loadId}`
}

export const getShipmentState = (shipmentState: string | undefined) => {
  switch (shipmentState) {
    case IShipmentStates.delivered:
      return 'Delivered'
    case IShipmentStates.arrived_for_delivery:
      return 'Arrived For Delivery'
    case IShipmentStates.arrived_for_pickup:
      return 'Arrived For Pickup'
    case IShipmentStates.in_transit:
      return 'In Transit'
    default:
      return 'Assigned'
  }
}

export const getArrivedAt = (
  shipmentDetails?: Maybe<ITrailerShipmentDetails>,
  arrivedAt?: Maybe<string>,
  facilityTimezone?: Maybe<string>
) => {
  const deliveryTime =
    shipmentDetails?.deliveryTrailerEmptiedAt ||
    shipmentDetails?.deliveryTrailerArrivedAt ||
    shipmentDetails?.deliveryArrivedAt

  const pickupTime =
    shipmentDetails?.pickupTrailerEmptiedAt ||
    shipmentDetails?.pickupTrailerArrivedAt ||
    shipmentDetails?.pickupArrivedAt

  if (deliveryTime) {
    return moment(deliveryTime).tz(shipmentDetails?.deliveryTimezone || FORMATS.defaultTimeZone)
  }

  if (pickupTime) {
    return moment(pickupTime).tz(shipmentDetails?.pickupTimezone || FORMATS.defaultTimeZone)
  }

  if (arrivedAt) {
    return moment(arrivedAt).tz(facilityTimezone || FORMATS.defaultTimeZone)
  }

  return DISPLAY.empty
}

export const getElapsedHours = (duration: number) => {
  const elapsedHours = Math.floor(duration)

  return elapsedHours > 1 ? `${elapsedHours} Hours` : `${elapsedHours} Hour`
}

export const getElapsedDays = (duration: number) => {
  const elapsedDays = Math.floor(duration)

  return elapsedDays > 1 ? `${elapsedDays} Days` : `${elapsedDays} Day`
}

export const getArrivedAtNote = (
  currentMoment: moment.Moment,
  arrivedAtMoment: moment.Moment,
  trailerState: ITrailerStatesEnum,
  shipmentState?: string
) => {
  if (
    trailerState === ITrailerStatesEnum.assigned_to_shipment &&
    shipmentState !== IShipmentStates.delivered
  ) {
    return getShipmentState(shipmentState)
  }
  if (!arrivedAtMoment || !arrivedAtMoment.isValid()) {
    return '—'
  }

  const momentDuration = moment.duration(currentMoment.diff(arrivedAtMoment))
  const durationHours = momentDuration.asHours()
  return durationHours > 24
    ? getElapsedDays(momentDuration.asDays())
    : getElapsedHours(durationHours)
}

export const getLoadLocation = (city?: string, state?: string) => {
  if (!state) {
    return city || DISPLAY.empty
  }
  if (!city) {
    return state || DISPLAY.empty
  }
  return `${city}, ${state}`
}

export const getTrailerType = (isDrop?: boolean) => {
  if (isDrop == null) {
    return DISPLAY.empty
  }
  return isDrop ? 'Drop Trailer' : 'Live Trailer'
}

export const getLatestEvent = (
  shipmentDetails?: Maybe<ITrailerShipmentDetails>,
  arrivedAt?: Maybe<string>
) => {
  const arrivedAtMoment = moment(getArrivedAt(shipmentDetails, arrivedAt))

  return arrivedAtMoment.isValid()
    ? arrivedAtMoment.format(FORMATS.trailerArrivedAtFormat)
    : undefined
}
